



















































import Vue from "vue";
import { RawLocation } from "vue-router";
import VueMask from "v-mask";
import Toasted from "vue-toasted";
import OtpSessionModel from "@/models/OtpSessionModel";
import session from "@/global/session";

Vue.use(VueMask);
Vue.use(Toasted, {
	position: "bottom-left",
	fullWidth: true,
	duration: 8000,
});

interface LoginCopy {
	title: string;
	phoneHeading: string;
	codeHeading: string;
	phoneIntro: string;
	phoneCta: string;
	codeCta: string;
}

// Preset copy per context. `default` is the fallback; a `?context=` param
// selects another set, and individual pieces can be overridden with
// ?title= / ?heading= / ?intro= query params.
const CONTEXTS: { [key: string]: LoginCopy } = {
	default: {
		title: "Sign In",
		phoneHeading: "",
		codeHeading: "Enter your code",
		phoneIntro: "Enter your mobile number and we'll text you a one-time code.",
		phoneCta: "Send code",
		codeCta: "Continue",
	},
	auction: {
		title: "Mobile Bidding",
		phoneHeading: "Sign in to bid",
		codeHeading: "Enter your code",
		phoneIntro: "Enter your mobile number and we'll text you a one-time code.",
		phoneCta: "Text me a code",
		codeCta: "Verify & start bidding",
	},
	admin: {
		title: "Admin Sign In",
		phoneHeading: "Verify your identity",
		codeHeading: "Enter your code",
		phoneIntro: "Enter your mobile number to receive a one-time access code.",
		phoneCta: "Text me a code",
		codeCta: "Verify & continue",
	},
	checkout: {
		title: "Checkout",
		phoneHeading: "Sign in to check out",
		codeHeading: "Enter your code",
		phoneIntro: "Enter the mobile number on file to receive a one-time code.",
		phoneCta: "Text me a code",
		codeCta: "Verify & continue",
	},
};

export default Vue.extend({
	data() {
		return {
			step: "phone" as "phone" | "code",
			phone: "" as string,
			code: "" as string,
			isBusy: false as boolean,
			devCode: null as string | null,
		};
	},

	computed: {
		// The dev OTP code is only ever shown on a local (development) build —
		// never on the deployed production bundle, even if the server sends it.
		isDevBuild(): boolean {
			return process.env.NODE_ENV === "development";
		},

		copy(): LoginCopy {
			const q = this.$route.query;
			const key = (q.context as string) || "default";
			const base = CONTEXTS[key] || CONTEXTS.default;

			// Per-piece overrides for one-off custom text via the URL.
			return {
				title: (q.title as string) || base.title,
				phoneHeading: (q.heading as string) || base.phoneHeading,
				codeHeading: base.codeHeading,
				phoneIntro: (q.intro as string) || base.phoneIntro,
				phoneCta: base.phoneCta,
				codeCta: base.codeCta,
			};
		},
	},

	methods: {
		async requestCode() {
			// The input mask enforces the FORMAT but not completeness, so a
			// partial entry like "(440) 241-689" still satisfies `required`.
			// Require a full 10-digit US number (or 11 with a leading country 1)
			// before sending, so we never fire off a code for a short number.
			const digits = this.phone.replace(/\D/g, "");
			if (digits.length !== 10 && !(digits.length === 11 && digits.charAt(0) === "1")) {
				this.$toasted.error("Please enter a complete 10-digit phone number.");
				return;
			}

			this.isBusy = true;
			this.devCode = null;

			try {
				const result = await OtpSessionModel.request(this.phone);

				if (result.reason === "rate_limited") {
					this.$toasted.error("Too many code requests. Please wait a few minutes and try again.");
				} else {
					// Always advance on ok — the API stays generic about unknown numbers.
					this.step = "code";
					if (result.devCode) this.devCode = result.devCode;
				}
			} catch (error) {
				this.$toasted.error("We couldn't send a code right now. Please try again.");
			}

			this.isBusy = false;
		},

		async verifyCode() {
			this.isBusy = true;

			try {
				const result = await OtpSessionModel.verify(this.phone, this.code);

				if (result.ok) {
					const redirect = (this.$route.query.redirect as string) || "/auction";
					// Don't send a non-admin into an admin route (avoids a guard loop).
					const target = redirect.indexOf("/admin") === 0 && !session.isAdmin() ? "/auction" : redirect;
					this.$router.push(target as RawLocation);
				} else if (result.reason === "locked") {
					this.$toasted.error("Too many attempts. This code is locked — request a new one shortly.");
				} else {
					this.$toasted.error("That code wasn't right. Please check it and try again.");
					this.code = "";
				}
			} catch (error) {
				this.$toasted.error("We couldn't verify that code. Please try again.");
			}

			this.isBusy = false;
		},

		resetToPhone() {
			this.step = "phone";
			this.code = "";
			this.devCode = null;
		},
	},
});
